import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";

export interface ApplianceCategory {
  id: string;
  name: string;
}

interface State {
  /** Categories the company has selected (in saved order). Falls back to the
   *  full master list when the company has not configured anything yet. */
  categories: ApplianceCategory[];
  /** Convenience: just the names, useful as Select option values. */
  names: string[];
  /** True when we are returning the full master list as a fallback. */
  usingFallback: boolean;
  loading: boolean;
  refresh: () => void;
}

const REFRESH_EVENT = "appliance-categories-changed";

/** Dispatch this after Company Settings save so live screens re-fetch. */
export function notifyApplianceCategoriesChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(REFRESH_EVENT));
  }
}

export function useCompanyApplianceCategories(): State {
  const { id: companyId, loading: companyLoading } = useCurrentCompany();
  const [categories, setCategories] = useState<ApplianceCategory[]>([]);
  const [usingFallback, setUsingFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const onEvent = () => refresh();
    window.addEventListener(REFRESH_EVENT, onEvent);
    return () => window.removeEventListener(REFRESH_EVENT, onEvent);
  }, [refresh]);

  useEffect(() => {
    if (companyLoading) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [masterRes, settingRes] = await Promise.all([
        supabase.from("appliance_categories").select("id, name").order("name"),
        companyId
          ? supabase
              .from("company_settings")
              .select("setting_value")
              .eq("company_id", companyId)
              .eq("setting_key", "appliance_categories")
              .maybeSingle()
          : Promise.resolve({ data: null, error: null } as any),
      ]);

      if (cancelled) return;

      const master = ((masterRes.data ?? []) as ApplianceCategory[]).filter(Boolean);
      const ids: string[] = Array.isArray((settingRes as any)?.data?.setting_value?.ids)
        ? (settingRes as any).data.setting_value.ids.filter((v: unknown): v is string => typeof v === "string")
        : [];

      if (ids.length === 0) {
        setCategories(master);
        setUsingFallback(true);
      } else {
        const map = new Map(master.map((c) => [c.id, c]));
        const ordered = ids.map((id) => map.get(id)).filter(Boolean) as ApplianceCategory[];
        setCategories(ordered);
        setUsingFallback(false);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, companyLoading, tick]);

  return {
    categories,
    names: categories.map((c) => c.name),
    usingFallback,
    loading,
    refresh,
  };
}
